import { storage } from "@core/storage/storage";

import { TutorialBannerService } from "@domain/tutorial-banner/tutorial-banner-service";
import { UserManager } from "@domain/user/user-manager";
import { getEnumValues } from "@utils/enum-utils";
import _ from "lodash";
import { Observable, observable } from "micro-observables";

export enum TutorialBannerType {
	MONITOR_PATIENT_PROGRAM = "MONITOR_PATIENT_PROGRAM",
	INVITE_TO_HCT = "INVITE_TO_HCT",
}

export const tutorialBannerTypes = getEnumValues(TutorialBannerType);

export type TutorialLinks = Record<TutorialBannerType, TutorialData | undefined>;

export interface TutorialData {
	videoUrl: string;
}

type TutorialBannersStoreType = { [userId: string]: TutorialBannerType[] };

export class TutorialBannerManager {
	private readonly _disabledBannersStore = storage.getStore<TutorialBannersStoreType, TutorialBannersStoreType>(
		"disabledBanners",
		v => v,
		v => v,
	);

	private readonly _disabledBanners = observable<TutorialBannerType[]>([]);

	private readonly _displayedBanners = observable<TutorialBannerType[]>([]);
	readonly displayedBanners = this._displayedBanners.readOnly();

	private readonly _tutorialLinks = observable<TutorialLinks | undefined>(undefined);

	constructor(
		private readonly tutorialBannerService: TutorialBannerService,
		private readonly userManager: UserManager,
	) {
		this._disabledBanners.subscribe(disabledBanners =>
			this._displayedBanners.update(displayedBanners =>
				displayedBanners.filter(bannerType => !disabledBanners.includes(bannerType)),
			),
		);
		userManager.user.subscribe(async () => {
			const links = await this.tutorialBannerService.getTutorialLinks();
			this._tutorialLinks.set(links);
			this._disabledBanners.set(
				(_.get(this._disabledBannersStore.getItem(), userManager.user.get()?.id ?? "") as
					| TutorialBannerType[]
					| undefined) ?? [],
			);
		});
	}

	displayBanner(type: TutorialBannerType): void {
		if (!this._disabledBanners.get().includes(type) && !this._displayedBanners.get().includes(type)) {
			this._displayedBanners.update(banners => [...banners, type]);
		}
	}

	hideBanners(): void {
		this._displayedBanners.set([]);
	}

	closeBanner(type: TutorialBannerType): void {
		this._displayedBanners.update(banners => banners.filter(banner => banner !== type));
		const newDisabledBanners = [...this._disabledBanners.get(), type];
		this.updateDisabledBannersAndCurrentUserStore(newDisabledBanners);
	}

	getTutorialLink(type: TutorialBannerType): TutorialData | undefined {
		return this._tutorialLinks.get()?.[type];
	}

	debugGetDisabledBanners(): Observable<TutorialBannerType[]> {
		return this._disabledBanners;
	}

	debugGetTutorials(): Observable<TutorialLinks | undefined> {
		return this._tutorialLinks;
	}

	debugResetBanners(): void {
		this.updateDisabledBannersAndCurrentUserStore([]);
	}

	private updateDisabledBannersAndCurrentUserStore(disabledBanners: TutorialBannerType[]): void {
		this._disabledBannersStore.setItem({
			...this._disabledBannersStore.getItem(),
			[this.userManager.user.get()?.id ?? ""]: disabledBanners,
		});
		this._disabledBanners.set(disabledBanners);
	}
}
